import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing service worker functionality
 * Provides offline status, update management, and PWA features
 */
export function useServiceWorker() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState(
    'Notification' in window ? Notification.permission : 'denied'
  );

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle PWA install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setIsInstallable(false);
      console.log('PWA was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Install PWA
  const installPWA = useCallback(async () => {
    if (!installPrompt) return false;

    try {
      const result = await installPrompt.prompt();
      console.log('Install prompt result:', result.outcome);
      
      if (result.outcome === 'accepted') {
        setInstallPrompt(null);
        setIsInstallable(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error installing PWA:', error);
      return false;
    }
  }, [installPrompt]);

  // Apply service worker update
  const applyUpdate = useCallback(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    }
  }, []);

  // Request notification permission
  const enableNotifications = useCallback(async () => {
    try {
      if (!('Notification' in window)) return null;
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      setNotificationPermission(granted ? 'granted' : 'denied');

      if (granted && 'serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: import.meta.env.VITE_VAPID_PUBLIC_KEY || '',
        });
        console.log('Push subscription:', subscription);
        return subscription;
      }
      return null;
    } catch (error) {
      console.error('Error enabling notifications:', error);
      return null;
    }
  }, []);

  // Cache specific URLs
  const cacheUrls = useCallback(async (urls) => {
    try {
      const cache = await caches.open('runtime-cache');
      await cache.addAll(urls);
      return true;
    } catch (error) {
      console.error('Error caching URLs:', error);
      return false;
    }
  }, []);

  // Clear specific cache
  const clearCache = useCallback(async (cacheName) => {
    try {
      await caches.delete(cacheName || 'runtime-cache');
      return true;
    } catch (error) {
      console.error('Error clearing cache:', error);
      return false;
    }
  }, []);

  // Get cached URLs
  const getCachedUrls = useCallback(async () => {
    try {
      const cache = await caches.open('runtime-cache');
      const requests = await cache.keys();
      return requests.map((request) => request.url);
    } catch (error) {
      console.error('Error getting cached URLs:', error);
      return [];
    }
  }, []);

  // Show custom notification
  const showNotification = useCallback((title, options = {}) => {
    if (notificationPermission === 'granted' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, {
          body: options.body || '',
          icon: options.icon || '/logo192.png',
          badge: options.badge || '/logo192.png',
          tag: options.tag || 'default',
          requireInteraction: options.requireInteraction || false,
          actions: options.actions || [],
          data: options.data || {},
          ...options
        });
      });
    }
  }, [notificationPermission]);

  // Check if app is running as PWA
  const isPWA = useCallback(() => {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true;
  }, []);

  // Get connection info
  const getConnectionInfo = useCallback(() => {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      };
    }
    return null;
  }, []);

  return {
    // Status
    isOnline,
    isUpdateAvailable,
    isInstallable,
    isPWA: isPWA(),
    notificationPermission,
    connectionInfo: getConnectionInfo(),

    // Actions
    installPWA,
    applyUpdate,
    enableNotifications,
    showNotification,
    cacheUrls,
    clearCache,
    getCachedUrls,
  };
}

/**
 * Hook for managing offline data synchronization
 */
export function useOfflineSync() {
  const [pendingSync, setPendingSync] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);

  // Add data to sync queue
  const addToSyncQueue = useCallback((data, endpoint, method = 'POST') => {
    const syncItem = {
      id: Date.now() + Math.random(),
      data,
      endpoint,
      method,
      timestamp: new Date().toISOString()
    };

    setPendingSync(prev => [...prev, syncItem]);

    // Store in localStorage for persistence
    const stored = JSON.parse(localStorage.getItem('offlineSyncQueue') || '[]');
    stored.push(syncItem);
    localStorage.setItem('offlineSyncQueue', JSON.stringify(stored));

    return syncItem.id;
  }, []);

  // Remove from sync queue
  const removeFromSyncQueue = useCallback((id) => {
    setPendingSync(prev => prev.filter(item => item.id !== id));
    
    const stored = JSON.parse(localStorage.getItem('offlineSyncQueue') || '[]');
    const filtered = stored.filter(item => item.id !== id);
    localStorage.setItem('offlineSyncQueue', JSON.stringify(filtered));
  }, []);

  // Sync all pending data
  const syncPendingData = useCallback(async () => {
    if (isSyncing || !navigator.onLine) return;

    setIsSyncing(true);
    const stored = JSON.parse(localStorage.getItem('offlineSyncQueue') || '[]');
    
    for (const item of stored) {
      try {
        const response = await fetch(item.endpoint, {
          method: item.method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(item.data)
        });

        if (response.ok) {
          removeFromSyncQueue(item.id);
        }
      } catch (error) {
        console.error('Sync failed for item:', item.id, error);
      }
    }

    setIsSyncing(false);
  }, [isSyncing, removeFromSyncQueue]);

  // Load pending sync items on mount
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('offlineSyncQueue') || '[]');
    setPendingSync(stored);
  }, []);

  // Auto-sync when coming online
  useEffect(() => {
    const handleOnline = () => {
      setTimeout(syncPendingData, 1000); // Delay to ensure connection is stable
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncPendingData]);

  return {
    pendingSync,
    isSyncing,
    addToSyncQueue,
    removeFromSyncQueue,
    syncPendingData
  };
}

export default useServiceWorker;
